import { ObservableLabels } from 'components/ImpressionAnalytics/types';
import { useClickCapture } from 'components/ImpressionAnalytics/useClickCapture';
import { useShowObserver } from 'components/ImpressionAnalytics/useShowObserver';
import React, { useMemo } from 'react';

type ImpressionAnalyticsContextType = {
  observe: (element: HTMLDivElement, labels: ObservableLabels) => void;
  captureClick: (labels: ObservableLabels) => void;
};

type Props = {
  children: React.ReactNode;
  uiElementPosition: number;
  uiElementType: string;
};

const ImpressionAnalyticsContext =
  React.createContext<ImpressionAnalyticsContextType>({
    get observe(): never {
      throw new Error('No providers for ImpressionAnalyticsContext');
    },
    get captureClick(): never {
      throw new Error('No providers for ImpressionAnalyticsContext');
    },
  });

const ImpressionAnalyticsContextProvider: React.FunctionComponent<Props> = ({
  children,
  uiElementPosition,
  uiElementType,
}) => {
  const observe = useShowObserver({ uiElementPosition, uiElementType });
  const captureClick = useClickCapture({ uiElementPosition, uiElementType });

  const contextValue = useMemo<ImpressionAnalyticsContextType>(
    () => ({ observe, captureClick }),
    [captureClick, observe]
  );

  return (
    <ImpressionAnalyticsContext.Provider value={contextValue}>
      {children}
    </ImpressionAnalyticsContext.Provider>
  );
};

export function useImpressionAnalyticsContext(): ImpressionAnalyticsContextType {
  return React.useContext(ImpressionAnalyticsContext);
}

export default ImpressionAnalyticsContextProvider;
